import { useMsal } from '@azure/msal-react'
import useLogin from '../hooks/useLogin'
import { useCallback, useLayoutEffect, useRef } from 'react'
import { InteractionStatus, type AccountInfo } from '@azure/msal-browser'
import { useNavigate } from 'react-router'

const AZURE_BACKEND_CLIENT_ID = import.meta.env.VITE_AZURE_BACKEND_CLIENT_ID

/**
 * React component that attempts to silently authenticate a user with Microsoft using MSAL.
 *
 * On mount, it tries to acquire an access token silently for any existing Microsoft account.
 * If silent authentication fails or no account is found, it displays a login button to allow
 * the user to manually sign in.
 */
const SilentMicrosoftLogin = () => {
  const { instance, inProgress } = useMsal()
  const { handleMicrosoftLogin } = useLogin({ variant: 'microsoft' })

  const initiadeSilentLogin = useRef(false)

  const navigate = useNavigate()

  /** Function to handle silent login */
  const handleSilentMicrosoftLogin = useCallback(
    (account: AccountInfo) => {
      instance
        .acquireTokenSilent({
          scopes: [`${AZURE_BACKEND_CLIENT_ID}/.default`],
          account: account,
        })
        .then(response => handleMicrosoftLogin(response.accessToken))
        .catch(error => {
          console.error('Silent login failed:', error)

          // Navigate to root (login page)
          void navigate('/', { replace: true })
        })
    },
    [handleMicrosoftLogin, instance, navigate]
  )

  /** Attempt silent login first, otherwise show login button */
  const attemptSilentLogin = useCallback(async () => {
    const accounts = instance.getAllAccounts()

    // If accounts are found, try silent login
    if (accounts.length > 0 && !!accounts[0])
      return handleSilentMicrosoftLogin(accounts[0])

    // Otherwise, navigate to root (login page)
    void navigate('/', { replace: true })
  }, [instance, handleSilentMicrosoftLogin, navigate])

  /** Attempt silent login on component mount */
  useLayoutEffect(() => {
    if (initiadeSilentLogin.current || inProgress !== InteractionStatus.None)
      return

    initiadeSilentLogin.current = true

    void attemptSilentLogin()
  }, [inProgress, attemptSilentLogin])

  return null
}

export default SilentMicrosoftLogin
